import moment from 'moment'
import 'moment/dist/locale/fr.js'

moment.locale('fr')

import { isReactive, toRaw, Comment, Text } from 'vue'

class Commons {
  static getFileNameFromUrl(url) {
    try {
      return new URL(url).pathname.split('/').pop()
    } catch (error) {
      console.error('URL invalide:', error)
      return ''
    }
  }

  static changeToJson(data) {
    try {
      return structuredClone(data)
    } catch (error) {
      // console.log(error, data)
      // console.log('will make toDeepRaw which will try to convert proxy of vue')
    }
    try {
      return toDeepRaw(data)
    } catch (error) {
      // console.log(error, data)
      // console.log('will make json stringify and parse')
    }
    try {
      return JSON.parse(JSON.stringify(data))
    } catch (error) {
      console.log('🚀 ~ Commons ~ changeToJson ~ error:', error)
      console.log('🚀 ~ Commons ~ changeToJson ~ data:', data)
    }
    return null
  }

  static time(time, dateFormat = 'DD MMMM YYYY', fallback = '-') {
    if (!time) return fallback
    if (/^\d+$/.test(time)) time = parseInt(`${time}`)
    return moment(new Date(time)).format(dateFormat)
  }

  static isInternalLink(link) {
    return typeof link === 'string' && link.startsWith('/')
  }

  static isImgString(data) {
    if (typeof data !== 'string') return false

    // extensions courantes d'images
    const imgExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg']

    return (
      data.startsWith('data:image/') || // inline base64
      imgExtensions.some(ext => data.toLowerCase().endsWith(ext))
    )
  }

  static log(...args) {
    console.log('🚀 ~ Commons ~ log ', ...args)
  }

  static isEmpty(data) {
    if (/(number)|(boolean)/.test(typeof data)) {
      return false
    }
    if (!data) {
      return true
    }
    if (Array.isArray(data)) {
      return data.length === 0
    }
    const dataProperties = Object.values(data)
    return dataProperties && dataProperties.length == 0
  }

  static infiniteLoading(callback, interval = 300) {
    let progress = 0

    const timer = setInterval(() => {
      progress += (100 - progress) / 100
      if (progress >= 99.999) {
        progress = 99.999 // On s'arrête juste avant 100
        clearInterval(timer) // Arrête l'intervalle si on est très proche de 100
      }
      callback(progress)
    }, interval)

    return () => clearInterval(timer)
  }

  static getMimeTypeFromUrl(url) {
    const mimeTypes = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      html: 'text/html',
      txt: 'text/plain',
      json: 'application/json',
      csv: 'text/csv',
      mp4: 'video/mp4',
      mp3: 'audio/mpeg',
      zip: 'application/zip',
      xml: 'application/xml',
    }

    try {
      const extension = new URL(url).pathname.split('.').pop().toLowerCase()
      return mimeTypes[extension] || 'application/octet-stream' // Valeur par défaut si non trouvé
    } catch (error) {
      console.error('URL invalide:', error)
      return 'Type inconnu'
    }
  }

  static jsonToHtml(obj) {
    const lines = flattenObject(obj)
    return lines
      .map(([k, v]) => `${escapeHtml(k)} -> ${escapeHtml(v)}<br/>`)
      .join('')
  }

  static slotHasContent(slotFn) {
    if (!slotFn) return false
    const nodes = slotFn()
    return nodes.some(vnode => {
      if (vnode.type === Comment) return false
      if (vnode.type === Text) {
        return vnode.children.trim() !== '' // texte réel accepté
      }
      return true // tout autre type (élément, composant) = vrai contenu
    })
  }

  static deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true
    if (
      typeof obj1 !== 'object' ||
      typeof obj2 !== 'object' ||
      obj1 == null ||
      obj2 == null
    )
      return false

    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)
    if (keys1.length !== keys2.length) return false

    for (const key of keys1) {
      if (!Commons.deepEqual(obj1[key], obj2[key])) return false
    }
    return true
  }

  static formatNumber(
    value,
    { locale = 'fr-FR', decimals = 0, compact = false, suffix = '' } = {},
  ) {
    if (value == null || isNaN(value)) return '—'

    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      notation: compact ? 'compact' : 'standard',
      compactDisplay: 'short',
    })

    return formatter.format(value) + suffix
  }

  static formatAmount(value) {
    return Commons.formatNumber(value, { decimals: 0 })
  }

  static truncate(text = '', maxLength = 37) {
    if (typeof text !== 'string') return ''
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + '...'
  }

  static generateUUID() {
    return crypto.randomUUID()
  }
}

function isObject(value) {
  return value !== null && !Array.isArray(value) && typeof value === 'object'
}

function getRawData(data) {
  return isReactive(data) ? toRaw(data) : data
}

function toDeepRaw(data) {
  const rawData = getRawData(data)

  for (const key in rawData) {
    const value = rawData[key]

    if (!isObject(value) && !Array.isArray(value)) {
      continue
    }

    rawData[key] = toDeepRaw(value)
  }

  return rawData // much better: structuredClone(rawData)
}

function escapeHtml(str) {
  if (str === null || str === undefined) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function flattenObject(obj, prefix = '') {
  const lines = []
  if (obj === null || obj === undefined) {
    lines.push([prefix.replace(/\.$/, ''), ''])
    return lines
  }
  if (typeof obj !== 'object' || obj instanceof Date) {
    lines.push([prefix.replace(/\.$/, ''), obj])
    return lines
  }
  Object.entries(obj).forEach(([key, value]) => {
    const newPrefix = prefix ? `${prefix}${key}.` : `${key}.`
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      lines.push(...flattenObject(value, newPrefix))
    } else {
      lines.push([newPrefix.replace(/\.$/, ''), value])
    }
  })
  return lines
}

export default Commons
